import React from "react";
import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableHead,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

function flattenElements(children) {
  const result = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      result.push(...flattenElements(child.props.children));
      return;
    }
    result.push(child);
  });
  return result;
}

function textFromNode(node) {
  if (typeof node === "string" || typeof node === "number") return String(node).trim();
  if (!React.isValidElement(node)) return "";
  return React.Children.toArray(node.props?.children).map(textFromNode).filter(Boolean).join(" ").trim();
}

function normalizeLabel(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function MobileRecordCard({ row, labels, collapseAfter, collapseDetails, essentialLabels }) {
  const [expanded, setExpanded] = React.useState(false);
  const cells = flattenElements(row.props?.children);
  const singleCell = cells.length === 1 && Number(cells[0]?.props?.colSpan || 1) > 1;

  if (singleCell) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderColor: "rgba(25,36,47,.10)" }}>
        {cells[0].props.children}
      </Paper>
    );
  }

  const normalizedEssentialLabels = new Set((essentialLabels || []).map(normalizeLabel));
  const records = cells.map((cell, index) => ({
    cell,
    label: labels[index] || `Dato ${index + 1}`,
    index,
  }));
  const actionPattern = /acci[oó]n|acciones|action/i;
  const criticalPattern = /estado|total|importe|monto|precio|stock|existencia|variaci[oó]n|costo|saldo|pendiente|pago|cobro|mensualidad|comisi[oó]n/i;
  const actionRecords = records.filter(({ label }) => actionPattern.test(label));
  const dataRecords = records.filter(({ label }) => !actionPattern.test(label));
  const isEssential = (label) => normalizedEssentialLabels.has(normalizeLabel(label));
  const shouldCollapse = collapseDetails && dataRecords.length > collapseAfter;
  const visibleRecords = shouldCollapse
    ? dataRecords.filter(({ label, index }) => index < collapseAfter || criticalPattern.test(label) || isEssential(label))
    : dataRecords;
  const hiddenRecords = shouldCollapse
    ? dataRecords.filter(({ label, index }) => index >= collapseAfter && !criticalPattern.test(label) && !isEssential(label))
    : [];

  const renderRecord = ({ cell, label, index }) => {
    const identityLabel = /cliente|usuario|publicaci[oó]n|producto|servicio|movimiento|comanda|pedido|cuenta|impuesto|proyecto|posici[oó]n|n[uú]mero|asiento|c[oó]digo/i.test(label);
    return (
      <Box key={`${label}-${index}`} sx={{ minWidth: 0 }}>
        {index > 0 || !identityLabel ? (
          <Typography variant="caption" sx={{ display: "block", mb: .25, color: "rgba(25,36,47,.52)", fontWeight: 850, lineHeight: 1.2 }}>
            {label}
          </Typography>
        ) : null}
        <Box sx={{ minWidth: 0, overflowWrap: "anywhere", "& .MuiStack-root": { maxWidth: "100%" } }}>
          {cell.props.children}
        </Box>
      </Box>
    );
  };

  return (
    <Paper
      variant="outlined"
      onClick={row.props?.onClick}
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        borderColor: "rgba(25,36,47,.10)",
        minWidth: 0,
        cursor: row.props?.onClick ? "pointer" : "default",
        ...(row.props?.sx || {}),
      }}
    >
      <Stack spacing={1.05}>
        {visibleRecords.map(renderRecord)}
        {hiddenRecords.length ? (
          <>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Stack spacing={1.05} sx={{ pt: .1 }}>
                {hiddenRecords.map(renderRecord)}
              </Stack>
            </Collapse>
            <Button
              size="small"
              variant="text"
              onClick={(event) => { event.stopPropagation(); setExpanded((value) => !value); }}
              endIcon={<ExpandMoreRoundedIcon sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .18s ease" }} />}
              sx={{ alignSelf: "flex-start", minHeight: 40, px: .5, textTransform: "none", fontWeight: 850 }}
            >
              {expanded ? "Ocultar detalles" : `Ver ${hiddenRecords.length} detalle${hiddenRecords.length === 1 ? "" : "s"}`}
            </Button>
          </>
        ) : null}
        {actionRecords.length ? (
          <Stack
            spacing={.75}
            sx={{
              pt: 1,
              mt: .15,
              borderTop: "1px solid rgba(25,36,47,.08)",
              "& .MuiButton-root": { minHeight: 44 },
              "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 },
            }}
          >
            {actionRecords.map(({ cell, label, index }) => (
              <Box key={`${label}-${index}`} onClick={(event) => event.stopPropagation()} sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: .75 }}>
                {cell.props.children}
              </Box>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function ResponsiveOperationalTable({
  children,
  mobileMode = "cards",
  collapseAfter = 3,
  collapseDetails = true,
  mobileEssentialLabels = [],
  mobileLabel = "Registros",
  sx,
  ...tableProps
}) {
  const parts = flattenElements(children);
  const head = parts.find((child) => child.type === TableHead);
  const body = parts.find((child) => child.type === TableBody);
  const headerRow = head ? flattenElements(head.props.children)[0] : null;
  const labels = headerRow
    ? flattenElements(headerRow.props.children).map((cell) => textFromNode(cell.props.children) || "Dato")
    : [];
  const rows = body ? flattenElements(body.props.children) : [];
  const canRenderCards = mobileMode === "cards" && head && body;

  return (
    <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Box sx={{ display: { xs: canRenderCards ? "none" : "block", md: "block" }, maxWidth: "100%", overflowX: mobileMode === "scroll" ? "auto" : "visible", WebkitOverflowScrolling: "touch", overscrollBehaviorX: "contain" }}>
        {mobileMode === "scroll" ? (
          <Typography variant="caption" sx={{ display: { xs: "block", md: "none" }, px: .25, pb: .75, color: "rgba(25,36,47,.56)", fontWeight: 750 }}>
            Deslizá horizontalmente para comparar columnas.
          </Typography>
        ) : null}
        <Table {...tableProps} sx={sx}>{children}</Table>
      </Box>

      {canRenderCards ? (
        <Stack role="list" aria-label={mobileLabel} spacing={1} sx={{ display: { xs: "flex", md: "none" }, p: .1 }}>
          {rows.map((row, index) => (
            <Box role="listitem" key={row.key || index} sx={{ minWidth: 0 }}>
              <MobileRecordCard
                row={row}
                labels={labels}
                collapseAfter={collapseAfter}
                collapseDetails={collapseDetails}
                essentialLabels={mobileEssentialLabels}
              />
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
